import axios, { AxiosResponse } from 'axios';

import { DataReq } from './types';

export type FetchParamsType = { [key: string]: string | undefined };

type CacheRecordType = { data?: AxiosResponse<DataReq<string>>; requestTime: number };

const DefaultInvalidateCacheTime = 60_000;
const DefaultMaxCacheLength = 100;

const CACHE: {
  [key: string]: CacheRecordType;
} = {};

function isParamsEmpty(params: FetchParamsType): boolean {
  return Object.values(params).every(value => !value);
}

function cleanCache() {
  for (const prop of Object.getOwnPropertyNames(CACHE)) {
    delete CACHE[prop];
  }
}

function createCacheKey(apiUrl: string, params: FetchParamsType) {
  const isNoParams = isParamsEmpty(params);
  const reqParams = isNoParams ? '' : `@${JSON.stringify(params)}`;
  return `${apiUrl}${reqParams}`;
}

function isNeedToFetch(cachedValue: CacheRecordType, invalidateCacheTime: number) {
  const isNeedRefetch = cachedValue && Date.now() - cachedValue.requestTime > invalidateCacheTime;
  return !cachedValue || isNeedRefetch;
}

export async function cachedRequest(
  axiosGet: typeof axios.get,
  apiUrl: string,
  params: FetchParamsType,
  invalidateCacheTime: number = DefaultInvalidateCacheTime,
  maxCacheLength: number = DefaultMaxCacheLength,
) {
  const cacheLength = Object.keys(CACHE).length;
  if (cacheLength >= maxCacheLength) {
    cleanCache();
  }

  const cacheKey = createCacheKey(apiUrl, params);
  const cachedValue = CACHE[cacheKey];

  if (isNeedToFetch(cachedValue, invalidateCacheTime)) {
    CACHE[cacheKey] = { requestTime: Date.now() };
    try {
      CACHE[cacheKey].data = await axiosGet(apiUrl, { params });
    } catch (err) {
      console.error(err);
    }
  }

  return CACHE[cacheKey].data;
}
